import { Eye, Edit2, Trash2, Check, RefreshCcw } from "lucide-react";
import { SearchBar, StatusBadge, EmptyState } from "../../index.jsx";
import { filterCompanies } from "../../utils/filter.js";
import { useState, useEffect } from "react";
import ConfirmDeleteModal from "./ConfirmDeleteModel.jsx";
import { companyService } from "../../../../services/index.js";
import Pagination from "../../../common/Pagination.jsx";

export default function CompaniesTable({
  companies,
  searchQuery,
  setSearchQuery,
  onSelect,
  isOnboarding,
  getCompanyList
}) {
  const getCompanyId = (item = {}) =>
    item.companyID ?? item.companyId ?? item.id ?? 0;

  const filtered = filterCompanies(companies, searchQuery);
  const [company, setCompany] = useState();
  const [showToast, setShowToast] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedData = filtered.slice(startIndex, startIndex + itemsPerPage);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, itemsPerPage]);

  const handleSelect = async (id, for_delete = false, for_edit = false, company_detail) => {
    setCompany(company_detail);
    if (for_delete) {
      return;
    }
    if (for_edit) {
      try{
        const companyResponse= await companyService.getById(id);
      if(companyResponse?.data)
      {
        isOnboarding(true);
        onSelect(companyResponse.data, false);
        return;
      }
      }catch (error) {
      console.error("Get company by ID error:", error);
    }
    isOnboarding(true);
    onSelect(company_detail, false);
    return;
    }
    // View mode — no isOnboarding, pass viewOnly=true
    onSelect(company_detail, true);
  };
  const handleDeleteConfirm = async () => {
      try {
        await companyService.delete({
          companyId: getCompanyId(company),
          deletedBy: localStorage.getItem("userId"),
        });
        setShowDeleteModal(false);
        setShowToast(true);
        setTimeout(() => {
          getCompanyList();
          setShowToast(false);
        }, 1500);
      } catch (error) {
        console.error("Error revoking user:", error);
        alert("Failed to revoke user. Please try again.");
      }
    };


  const handleRefresh = async () => {
    setIsRefreshing(true);
    
    setTimeout(() => {
      getCompanyList();
      setIsRefreshing(false);
    }, 500);
  };
  return (
    <div className="bg-white dark:bg-slate-800 rounded-[2rem] shadow-sm border border-slate-100 dark:border-slate-700 overflow-hidden flex flex-col h-auto max-h-[calc(100vh-140px)]">
      <div className="p-5 border-b border-slate-100 dark:border-slate-700 flex flex-col sm:flex-row sm:items-center justify-between gap-4 shrink-0">
        <h3 className="text-lg font-semibold text-slate-800 dark:text-white ml-2">
          Company Directory
        </h3>
        
        <div className="flex flex-col sm:flex-row items-center gap-4">
          {/* <div className="relative group/refresh">
            <RefreshCcw
              size={20}
              className={`text-slate-400 dark:text-slate-500 hover:text-slate-500 dark:hover:text-slate-400 cursor-pointer transition-transform ${
                isRefreshing ? "animate-spin" : ""
              }`}
              onClick={handleRefresh}
            />
            <span className="absolute -bottom-7 left-1/2 -translate-x-1/2 text-[11px] font-medium bg-[#f4f6f8] dark:bg-slate-900/50 text-slate-700 dark:text-slate-200 px-2 py-0.5 rounded-md opacity-0 group-hover/refresh:opacity-100 transition-opacity pointer-events-none whitespace-nowrap">
              Refresh
            </span>
          </div> */}
          <Pagination 
            currentPage={currentPage} 
            totalPages={totalPages} 
            onPageChange={setCurrentPage} 
            itemsPerPage={itemsPerPage} 
            onItemsPerPageChange={setItemsPerPage} 
          />

          <SearchBar
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search by name, email, or GST…"
          />
        </div>
      </div>

      <div className="w-full flex-1 overflow-auto hide-scrollbar">
        <table className="w-full text-left border-collapse min-w-[800px]">
          <thead>
            <tr className="border-b border-slate-100 dark:border-slate-700 text-slate-500 dark:text-slate-400 text-sm">
              <th className="px-6 py-4 font-medium whitespace-nowrap">
                Company
              </th>
              <th className="px-6 py-4 font-medium whitespace-nowrap">
                GST No.
              </th>
              <th className="px-6 py-4 font-medium whitespace-nowrap">
                FileAnalytics
              </th>
              <th className="px-6 py-4 font-medium whitespace-nowrap">
                SQLAnalytics
              </th>
              <th className="px-6 py-4 font-medium whitespace-nowrap">
                MediaAnalytics
              </th>
              <th className="px-6 py-4 font-medium whitespace-nowrap">
                Status
              </th>
              <th className="px-6 py-4 font-medium text-center whitespace-nowrap">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
            {paginatedData.length > 0 ? (
              paginatedData.map((company, index) => (
                <tr
                  key={index}
                  className="hover:bg-slate-50/50 dark:hover:bg-slate-700/30 transition-colors group cursor-pointer"
                >
                  <td className="px-6 py-4">
                    <div className="font-semibold text-slate-800 dark:text-slate-200 whitespace-nowrap group-hover:text-[#1d57a5] dark:group-hover:text-[#4a8ce3] transition-colors">
                      {company.companyName}
                    </div>
                    <div className="text-slate-500 dark:text-slate-400 text-xs mt-0.5 whitespace-nowrap">
                      {company.officialEmail}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-slate-600 dark:text-slate-400 text-sm font-mono whitespace-nowrap">
                    {company.gstNo}
                  </td>
                  <td className="px-6 py-4">
                    {company.fileAnalyticsStatus?.toLowerCase() === "success" ? (
                      <span className="inline-flex items-center justify-center px-3.5 py-1 rounded-full bg-[#00d0e4]/10 text-[#00d0e4] border border-[#00d0e4]/20 text-[11px] font-bold tracking-wide">
                        Connected
                      </span>
                    ) : company.fileAnalyticsStatus?.toLowerCase() === "running" ? (
                      <span className="inline-flex items-center justify-center px-3.5 py-1 rounded-full bg-yellow-100 text-yellow-600 border border-yellow-200 text-[11px] font-bold tracking-wide">
                        Running
                      </span>
                    ) : company.fileAnalyticsStatus?.toLowerCase() === "failed" ? (
                      <span className="inline-flex items-center justify-center px-3.5 py-1 rounded-full bg-red-100 text-red-500 border border-red-200 text-[11px] font-bold tracking-wide">
                        Failed
                      </span>
                    ) : (
                      <span className="inline-flex items-center justify-center px-4 py-1 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 text-[11px] font-bold tracking-wide">
                        N/A
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    {company.sqlAnalyticsStatus?.toLowerCase() == "success" ? (
                      <span className="inline-flex items-center justify-center px-3.5 py-1 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-500 border border-amber-200 dark:border-amber-800/50 text-[11px] font-bold tracking-wide">
                        Connected
                      </span>
                    ) : (
                      <span className="inline-flex items-center justify-center px-4 py-1 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 text-[11px] font-bold tracking-wide">
                        N/A
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    {company.mediaAnalyticsStatus?.toLowerCase() == "success" ? (
                      <span className="inline-flex items-center justify-center px-3.5 py-1 rounded-full bg-[#36a852]/10 text-[#36a852] border border-[#36a852]/20 text-[11px] font-bold tracking-wide">
                        Connected
                      </span>
                    ) : (
                      <span className="inline-flex items-center justify-center px-4 py-1 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 text-[11px] font-bold tracking-wide">
                        N/A
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <span
                      className={`inline-flex items-center justify-center px-3 py-1 rounded-full text-[11px] font-bold tracking-wide ${
                        company.companyStatus == 1
                          ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                          : "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
                      }`}
                    >
                      {company.companyStatus == 1 ? "ACTIVE" : "INACTIVE"}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-center">
  <div className="flex items-center justify-center">
    
    <div className="flex items-center gap-1 bg-slate-100 dark:bg-slate-800 rounded-xl p-1 shadow-sm">
      
      {/* View */}
      <button
        className="p-2 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-white hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg transition-colors"
        title="View"
        onClick={() => handleSelect(getCompanyId(company), false, false, company)}
      >
        <Eye size={18} />
      </button>

      {/* Edit */}
      <button
        className="p-2 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-white hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg transition-colors"
        title="Edit"
        onClick={() => handleSelect(getCompanyId(company), false, true, company)}
      >
        <Edit2 size={16} />
      </button>

      {/* Delete */}
      <button
        className="p-2 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 hover:bg-red-100 dark:hover:bg-red-900/40 rounded-lg transition-colors"
        title="Delete"
        onClick={() => {
          handleSelect(getCompanyId(company), true, false, company);
          setShowDeleteModal(true);
        }}
      >
        <Trash2 size={16} />
      </button>

    </div>

  </div>
</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={6}>
                  <EmptyState query={searchQuery} label="companies" />
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {
        company && (
          <ConfirmDeleteModal
            isOpen={showDeleteModal}
            onClose={() => setShowDeleteModal(false)}
            onConfirm={handleDeleteConfirm}
            companyName={company.companyName}
          />  
        )
      }

      {showToast && (
        <div className="fixed top-6 right-6 z-[9999] bg-slate-900 dark:bg-white text-white dark:text-slate-900 px-6 py-4 rounded-2xl shadow-2xl flex items-center gap-3 animate-in slide-in-from-top-4 duration-300">
          <div className="w-7 h-7 rounded-full bg-[#36a852] flex items-center justify-center flex-shrink-0">
            <Check size={14} strokeWidth={3} />
          </div>
          <span className="text-sm font-semibold">Company revoked successfully!</span>
        </div>
      )}
    </div>
  );
}
